import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Space, Modal, Form, Input, Select, Tag, Switch, message, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UserOutlined } from '@ant-design/icons';
import { adminUserApi, roleApi, groupApi } from '../services/api';

export default function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [uRes, rRes, gRes] = await Promise.all([
        adminUserApi.list(), roleApi.list(), groupApi.list()
      ]);
      if (uRes.success) setUsers(uRes.data || []);
      if (rRes.success) setRoles(rRes.data || []);
      if (gRes.success) setGroups(gRes.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const openModal = (user) => {
    setEditingUser(user);
    setModalOpen(true);
    if (user) {
      form.setFieldsValue({ nickname: user.nickname, role_id: user.role_id, group_id: user.group_id, trade_enabled: user.trade_enabled === 1, status: user.status });
    } else {
      form.resetFields();
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      // 将 Switch 的 boolean 转为 int
      const payload = { ...values, trade_enabled: values.trade_enabled ? 1 : 0 };
      setSaving(true);
      let res;
      if (editingUser) {
        res = await adminUserApi.update(editingUser.id, payload);
      } else {
        res = await adminUserApi.create(payload);
      }
      if (res.success) {
        message.success(editingUser ? '更新成功' : '创建成功');
        setModalOpen(false);
        form.resetFields();
        loadData();
      } else {
        message.error(res.message);
      }
    } catch (e) { if (e.errorFields) return; message.error('操作失败'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    const res = await adminUserApi.delete(id);
    if (res.success) { message.success('已删除'); loadData(); }
    else message.error(res.message);
  };

  const columns = [
    { title: '用户名', dataIndex: 'username', width: 120 },
    { title: '昵称', dataIndex: 'nickname', width: 120 },
    { title: '角色', dataIndex: 'role_name', width: 100, render: (v, r) => <Tag color={r.role === 'admin' ? 'red' : 'blue'}>{v || r.role || '无'}</Tag> },
    { title: '关联分组', dataIndex: 'group_name', width: 120, render: v => v || '-' },
    { title: '交易权限', dataIndex: 'trade_enabled', width: 80, render: v => <Tag color={v === 1 ? 'green' : 'red'}>{v === 1 ? '开启' : '关闭'}</Tag> },
    { title: '状态', dataIndex: 'status', width: 80, render: v => <Tag color={v === 'active' ? 'green' : 'default'}>{v === 'active' ? '正常' : '禁用'}</Tag> },
    { title: '操作', width: 120, render: (_, r) => (
      <Space>
        <Button size="small" icon={<EditOutlined />} onClick={() => openModal(r)}>编辑</Button>
        {r.username !== 'admin' && (
          <Popconfirm title="确认删除？" onConfirm={() => handleDelete(r.id)}><Button size="small" danger icon={<DeleteOutlined />} /></Popconfirm>
        )}
      </Space>
    )},
  ];

  return (
    <div>
      <Card size="small" title={<Space><UserOutlined /> 用户管理</Space>}
        extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => openModal(null)}>新建用户</Button>}>
        <Table dataSource={users} columns={columns} rowKey="id" size="small" loading={loading} pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }} />
      </Card>

      <Modal title={editingUser ? '编辑用户' : '新建用户'} open={modalOpen} onOk={handleSave}
        onCancel={() => { setModalOpen(false); form.resetFields(); }} confirmLoading={saving}
        okText="确认" cancelText="取消" destroyOnClose>
        <Form form={form} layout="vertical">
          {!editingUser && (
            <>
              <Form.Item name="username" label="用户名" rules={[{ required: true, message: '请输入用户名' }]}>
                <Input placeholder="输入用户名" />
              </Form.Item>
              <Form.Item name="password" label="密码" rules={[{ required: true, message: '请输入密码' }, { min: 6, message: '密码至少6位' }]}>
                <Input.Password placeholder="输入密码" />
              </Form.Item>
            </>
          )}
          {editingUser && (
            <Form.Item name="password" label="新密码（留空不修改）">
              <Input.Password placeholder="留空则不修改密码" />
            </Form.Item>
          )}
          <Form.Item name="nickname" label="昵称">
            <Input placeholder="输入昵称" />
          </Form.Item>
          <Form.Item name="role_id" label="角色" rules={[{ required: true, message: '请选择角色' }]}>
            <Select placeholder="选择角色" options={roles.map(r => ({ value: r.id, label: r.name }))} />
          </Form.Item>
          <Form.Item name="group_id" label="关联客户端分组">
            <Select placeholder="选择分组（可选）" allowClear options={groups.map(g => ({ value: g.id, label: g.name }))} />
          </Form.Item>
          <Form.Item name="trade_enabled" label="交易权限" valuePropName="checked">
            <Switch checkedChildren="开启" unCheckedChildren="关闭" />
          </Form.Item>
          {editingUser && (
            <Form.Item name="status" label="状态">
              <Select options={[{ value: 'active', label: '正常' }, { value: 'disabled', label: '禁用' }]} />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
}
